import type { ReactNode } from 'react';
import { CustomerAvatar } from './CustomerAvatar';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { isPlaceholderEmail } from './syntheticEmail';

// 11c the profile · 11d the thin record. ONE component: the thin case is a
// variant, not a page. Forking them is how the thin case quietly grows an
// "N/A" block back — which is the exact thing 11d exists to delete.
//
// `signals === null` is the switch, and nothing else decides it.

export interface ProfileSignals {
  openNow: number;
  openBreaching: number;
  /** '1 breaching in 41m' — null when nothing is breaching. */
  breachingInLabel: string | null;
  lifetimeTickets: number;
  /** '4 billing · 3 SSO · 4 other' — null when there is no mix to state. */
  ticketMix: string | null;
  /** '1d 4h', or null when nothing has been resolved yet. */
  avgResolution: string | null;
  /** '2× the team median' — warn-toned. Null when the median is unknown. */
  avgResolutionNote: string | null;
  satisfaction: number | null;
  ratingCount: number;
  sinceLabel: string;
  tenureLabel: string;
}

export interface ProfileThin {
  /** 'ADDED 7 SEP · MANUAL ENTRY' */
  addedLabel: string;
  recordAgeDays: number;
  domain: string;
  domainIsDisposable: boolean;
  /** Other live records on the same domain. -1 while still counting. */
  domainSharedBy: number;
}

export interface ProfileHeaderProps {
  name: string;
  email: string;
  phone: string | null;
  avatarInitials: string;
  status: string;
  tags: string[];
  organisation: { id: string; name: string } | null;
  /** '21:32 IST', or null when the record carries no timezone. */
  localTime: string | null;
  timezone: string | null;
  locale: string | null;
  duplicateCount: number;
  signals: ProfileSignals | null;
  thin: ProfileThin | null;
  activeTab: string;
  tabCounts: { tickets: number; history: number; notes: number };
  onTab: (tab: string) => void;
  onBack: () => void;
  onOpenOrganisation: (id: string) => void;
  onFindDuplicates: () => void;
  onEdit: () => void;
  onStartChat: () => void;
  onNewTicket: () => void;
  onReviewSimilar: (domain: string) => void;
  onAssignOrganisation: () => void;
  canEdit: boolean;
  blocked: boolean;
  /** The record has been merged away into a primary. Read-only audit record:
   *  shows a MERGED chip and disables the identity actions. */
  merged?: boolean;
  actions?: ReactNode;
}

/** Floating card, matching the ticket pages. */
const FLOAT: React.CSSProperties = { borderRadius: 14, boxShadow: 'var(--co-shadow-card)' };

export function ProfileHeader(p: ProfileHeaderProps) {
  const thin = p.signals === null && p.thin !== null;

  return (
    <div className="flex flex-none flex-col gap-[14px]">
      {/* The duplicate warning lives in the breadcrumb, not the body: it is a
          fact about the record's IDENTITY, and everything below it is suspect
          until it is resolved. */}
      <div
        className="flex flex-wrap items-center justify-between gap-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] px-[18px] py-3"
        style={FLOAT}
      >
        {/* One row: breadcrumb on the left; record actions + utility cluster on the right. */}
        <div className="flex min-w-0 items-center gap-2 font-mono text-[11px] tracking-[.13em]">
          <button type="button" onClick={p.onBack} className="flex-none font-bold text-[var(--co-ink)] hover:text-[var(--co-brass-ink)]">
            ← CUSTOMERS
          </button>
          <span className="flex-none text-[var(--co-line-strong)]">/</span>
          <span className="truncate font-medium text-[var(--co-ink-3)]">{p.name.toUpperCase()}</span>
        </div>
        <div className="flex flex-none flex-wrap items-center gap-[10px]">
          {p.duplicateCount > 0 && (
            <button
              type="button"
              onClick={p.onFindDuplicates}
              disabled={p.blocked || !!p.merged}
              className="flex h-[34px] items-center rounded-[10px] border px-3 text-[12.5px] disabled:cursor-not-allowed disabled:opacity-50"
              style={{
                borderColor: 'var(--co-terracotta)',
                background: 'var(--co-terracotta-wash)',
                color: 'var(--co-terracotta-ink)',
              }}
            >
              {`${p.duplicateCount} possible duplicate${p.duplicateCount === 1 ? '' : 's'}`}
            </button>
          )}
          {p.canEdit && (
            <button
              type="button"
              onClick={p.onEdit}
              className="flex h-[34px] items-center rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)]"
            >
              Edit
            </button>
          )}
          {p.actions}
          {(p.duplicateCount > 0 || p.canEdit || p.actions) && (
            <span aria-hidden="true" className="mx-1 h-5 w-px flex-none bg-[var(--co-line)]" />
          )}
          <div className="flex h-[34px] flex-none items-center">
            <PageHeaderMeta />
          </div>
        </div>
      </div>

      {/* Identity card — identity band + signal row share one floating card. */}
      <div className="overflow-hidden border border-[var(--co-line)] bg-[var(--co-raised)]" style={FLOAT}>
      <div className="px-[24px] py-[20px]">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="flex min-w-0 items-start gap-4">
            <CustomerAvatar
              initials={p.avatarInitials}
              size={thin ? 46 : 52}
              variant={p.blocked ? 'blocked' : thin ? 'outlined' : 'filled'}
            />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <h1
                  className="leading-[1.05] text-[var(--co-ink)]"
                  style={{ fontFamily: 'var(--co-font-display)', fontSize: thin ? 26 : 30 }}
                >
                  {p.name}
                </h1>
                {p.merged ? (
                  // A merged-away record must never read as a normal customer,
                  // whatever its engagement signals say.
                  <StatusChip tone="warn">MERGED</StatusChip>
                ) : thin ? (
                  <StatusChip tone="warn">NEVER CONTACTED</StatusChip>
                ) : (
                  <>
                    <StatusChip tone={p.blocked ? 'breach' : 'met'}>{p.status.toUpperCase()}</StatusChip>
                    {p.tags.slice(0, 3).map((t) => (
                      <StatusChip key={t} tone="plain">
                        {t.toUpperCase()}
                      </StatusChip>
                    ))}
                  </>
                )}
              </div>

              <div className="mt-3 flex flex-wrap items-baseline gap-[22px]">
                {/* Voice-created customers carry a synthetic
                    phone_<n>@voice.centraoneplus.local placeholder (email is
                    required on the record). It is not a real address, so show
                    "no email on file" rather than present the placeholder. */}
                <Meta
                  label="EMAIL"
                  value={isPlaceholderEmail(p.email) ? 'no email on file' : p.email}
                />
                {p.phone && <Meta label="PHONE" value={p.phone} />}
                {p.organisation ? (
                  <Meta
                    label="ORG"
                    value={p.organisation.name}
                    onClick={() => p.onOpenOrganisation(p.organisation!.id)}
                  />
                ) : thin ? null : (
                  <Meta label="ORG" value="—" />
                )}
                {thin ? (
                  <span className="font-mono text-[11px] tracking-[.08em] text-[var(--co-ink-4)]">
                    {p.thin!.addedLabel}
                  </span>
                ) : (
                  // The first thing an agent decides is whether to call now.
                  p.localTime && <Meta label="LOCAL TIME" value={p.localTime} />
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-none items-center gap-2">
            <button
              type="button"
              onClick={p.onStartChat}
              disabled={p.blocked || !!p.merged}
              className="flex h-[34px] items-center rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] disabled:cursor-not-allowed disabled:text-[var(--co-ink-4)]"
            >
              Start chat
            </button>
            <button
              type="button"
              onClick={p.onNewTicket}
              disabled={p.blocked || !!p.merged}
              className="flex h-[34px] items-center rounded-[10px] px-4 text-[12.5px] font-semibold disabled:cursor-not-allowed"
              style={{
                background: p.blocked || p.merged ? 'var(--co-line-strong)' : 'var(--co-ink)',
                color: p.blocked || p.merged ? 'var(--co-raised)' : 'var(--co-ink-invert)',
              }}
            >
              New ticket
            </button>
          </div>
        </div>
      </div>

      {thin ? <ThinBody t={p.thin!} merged={!!p.merged} onReviewSimilar={p.onReviewSimilar} onAssignOrganisation={p.onAssignOrganisation} onStartChat={p.onStartChat} timezone={p.timezone} locale={p.locale} /> : <SignalRow s={p.signals!} />}
      </div>

      <Tabs active={p.activeTab} counts={p.tabCounts} onTab={p.onTab} />
    </div>
  );
}

/** 11c §4.3 — every figure carries its qualifier. `4.6` is a number; `4.6 from
 *  7 ratings` is a number you know how much to trust. */
function SignalRow({ s }: { s: ProfileSignals }) {
  return (
    <div className="flex flex-wrap items-stretch gap-y-4 border-t border-[var(--co-line-subtle)] bg-[var(--co-raised)] px-[24px] py-[18px]">
      <Signal
        first
        label="OPEN NOW"
        value={String(s.openNow)}
        note={s.breachingInLabel ?? (s.openNow === 0 ? 'nothing open' : 'none breaching')}
        valueTone={s.openBreaching > 0 ? 'breach' : undefined}
      />
      <Signal
        label="LIFETIME TICKETS"
        value={String(s.lifetimeTickets)}
        note={s.ticketMix ?? (s.lifetimeTickets === 0 ? 'none yet' : null)}
      />
      <Signal
        label="AVG RESOLUTION"
        value={s.avgResolution ?? '—'}
        note={s.avgResolutionNote ?? (s.avgResolution ? null : 'nothing resolved yet')}
        noteTone={s.avgResolutionNote ? 'warn' : undefined}
      />
      <Signal
        label="SATISFACTION"
        value={s.satisfaction === null ? '—' : s.satisfaction.toFixed(1)}
        note={s.ratingCount > 0 ? `from ${s.ratingCount} rating${s.ratingCount === 1 ? '' : 's'}` : 'no ratings yet'}
      />
      <Signal last label="CUSTOMER SINCE" value={s.sinceLabel} note={s.tenureLabel} />
    </div>
  );
}

/** 11d — four statistics reading "N/A" said nothing. The absence IS the finding. */
function ThinBody({
  t,
  timezone,
  locale,
  merged,
  onReviewSimilar,
  onAssignOrganisation,
  onStartChat,
}: {
  t: ProfileThin;
  timezone: string | null;
  locale: string | null;
  // A merged-away record is a read-only audit record — its "take action"
  // prompts (start a conversation, assign an org, review similar) are disabled.
  merged?: boolean;
  onReviewSimilar: (domain: string) => void;
  onAssignOrganisation: () => void;
  onStartChat: () => void;
}) {
  return (
    <div className="border-t border-[var(--co-line-subtle)] px-[24px] py-[18px]">
      <div
        className="rounded-[12px] border px-[18px] py-[16px]"
        style={{ borderColor: 'var(--co-terracotta)', background: 'var(--co-terracotta-wash)' }}
      >
        <div className="flex items-start gap-3">
          <span
            className="flex h-[18px] w-[18px] flex-none items-center justify-center text-[11px] font-semibold"
            style={{
              color: 'var(--co-terracotta-ink)',
              boxShadow: 'inset 0 0 0 1px var(--co-terracotta)',
            }}
            aria-hidden="true"
          >
            i
          </span>
          <div className="min-w-0">
            <div className="font-mono text-[9.5px] font-semibold tracking-[.16em] text-[var(--co-terracotta-ink)]">
              NOTHING TO SHOW YET — AND THAT&rsquo;S THE FINDING
            </div>
            <p
              className="mt-2 max-w-[86ch] text-[13px] leading-[1.55] text-[var(--co-ink-2)]"
              style={{ textWrap: 'pretty' } as React.CSSProperties}
            >
              No tickets, chats or calls since this record was created, no organisation
              {/* The domain clause only appears when the domain is a KNOWN
                  disposable provider and the count has actually been made —
                  an unrecognised domain says nothing rather than accusing a
                  real company. */}
              {t.domainIsDisposable && t.domainSharedBy >= 0 && (
                <>
                  , and the domain <strong className="text-[var(--co-ink)]">{t.domain}</strong> is a
                  disposable-mail provider
                  {t.domainSharedBy > 0
                    ? ` shared by ${t.domainSharedBy} other record${t.domainSharedBy === 1 ? '' : 's'}`
                    : ' used by no other record'}
                </>
              )}
              . Four statistics reading &ldquo;N/A&rdquo; said none of that.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={onStartChat}
                disabled={merged}
                className="flex h-[30px] items-center rounded-[10px] bg-[var(--co-ink)] px-3 text-[12.5px] font-semibold text-[var(--co-ink-invert)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Start a conversation
              </button>
              <button
                type="button"
                onClick={onAssignOrganisation}
                disabled={merged}
                className="flex h-[30px] items-center rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] text-[var(--co-ink-2)] hover:border-[var(--co-ink)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-[var(--co-line-strong)]"
              >
                Assign organisation
              </button>
              {t.domainSharedBy > 0 && (
                <button
                  type="button"
                  onClick={() => onReviewSimilar(t.domain)}
                  disabled={merged}
                  className="flex h-[30px] items-center rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] text-[var(--co-ink-2)] hover:border-[var(--co-ink)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-[var(--co-line-strong)]"
                >
                  {`Review ${t.domainSharedBy} similar record${t.domainSharedBy === 1 ? '' : 's'}`}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* The two zeros are dimmed and the two facts that DO say something are
          not. That inversion is the whole artboard. */}
      <div className="mt-[18px] flex items-stretch border-t border-[var(--co-line)] pt-[16px]">
        <Signal first muted label="TICKETS" value="0" />
        <Signal muted label="CONVERSATIONS" value="0" />
        <Signal label="RECORD AGE" value={`${t.recordAgeDays}d`} />
        <Signal
          last
          label="TIMEZONE · LOCALE"
          value={[timezone, locale].filter(Boolean).join(' · ') || 'not set'}
          valueSize={14}
        />
      </div>
    </div>
  );
}

function Signal({
  label,
  value,
  note,
  valueTone,
  noteTone,
  muted,
  first,
  last,
  valueSize = 22,
}: {
  label: string;
  value: string;
  note?: string | null;
  valueTone?: 'breach';
  noteTone?: 'warn';
  muted?: boolean;
  first?: boolean;
  last?: boolean;
  valueSize?: number;
}) {
  return (
    <>
      {!first && <div className="w-px flex-none self-stretch bg-[var(--co-line)]" />}
      <div
        className="min-w-0 flex-1"
        style={{ paddingLeft: first ? 0 : 30, paddingRight: last ? 0 : 30 }}
      >
        <div className="font-mono text-[9px] font-semibold tracking-[.16em] text-[var(--co-ink-4)]">
          {label}
        </div>
        <div className="mt-2 flex flex-wrap items-baseline gap-2">
          <span
            className="font-mono font-semibold tabular-nums"
            style={{
              fontSize: valueSize,
              color: muted
                ? 'var(--co-line-strong)'
                : valueTone === 'breach'
                  ? 'var(--co-burgundy)'
                  : 'var(--co-ink)',
            }}
          >
            {value}
          </span>
          {note && (
            <span
              className="text-[11.5px]"
              style={{ color: noteTone === 'warn' ? 'var(--co-terracotta-ink)' : 'var(--co-ink-3)' }}
            >
              {note}
            </span>
          )}
        </div>
      </div>
    </>
  );
}

function Tabs({
  active,
  counts,
  onTab,
}: {
  active: string;
  counts: { tickets: number; history: number; notes: number };
  onTab: (tab: string) => void;
}) {
  const tabs: [string, number | null][] = [
    ['overview', null],
    ['tickets', counts.tickets],
    ['history', counts.history],
    ['notes', counts.notes],
  ];
  return (
    <div
      className="flex items-end border border-[var(--co-line)] bg-[var(--co-raised)] px-[12px]"
      style={FLOAT}
    >
      {tabs.map(([id, n]) => {
        const on = id === active;
        return (
          <button
            key={id}
            type="button"
            aria-current={on ? 'page' : undefined}
            onClick={() => onTab(id)}
            className={`flex items-center gap-[6px] border-b-2 px-[16px] pb-[12px] pt-[14px] text-[14.5px] font-medium leading-none transition-colors duration-[120ms] ${
              on
                ? 'border-[var(--co-brass)] text-[var(--co-brass-ink)]'
                : 'border-transparent text-[var(--co-ink-3)] hover:text-[var(--co-ink)]'
            }`}
          >
            {id.charAt(0).toUpperCase() + id.slice(1)}
            {n !== null && (
              <span
                className={`rounded-full px-1.5 py-0.5 text-[11px] ${
                  on ? 'bg-[var(--co-brass-wash)] text-[var(--co-brass-ink)]' : 'bg-[var(--co-canvas)] text-[var(--co-ink-4)]'
                }`}
              >
                {n}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function StatusChip({ children, tone }: { children: ReactNode; tone: 'met' | 'warn' | 'breach' | 'plain' }) {
  const style =
    tone === 'met'
      ? { borderColor: 'var(--co-green-line)', background: 'var(--co-green-wash)', color: 'var(--co-green-ink)' }
      : tone === 'warn'
        ? {
            borderColor: 'var(--co-terracotta)',
            background: 'var(--co-terracotta-wash)',
            color: 'var(--co-terracotta-ink)',
          }
        : tone === 'breach'
          ? {
              borderColor: 'var(--co-burgundy-line)',
              background: 'var(--co-burgundy-wash)',
              color: 'var(--co-burgundy)',
            }
          : { borderColor: 'var(--co-line-strong)', color: 'var(--co-ink-2)' };
  return (
    <span
      className="flex-none rounded-full border px-[9px] py-[4px] font-mono text-[9.5px] font-semibold tracking-[.13em]"
      style={style}
    >
      {children}
    </span>
  );
}

function Meta({ label, value, onClick }: { label: string; value: string; onClick?: () => void }) {
  return (
    <div className="min-w-0">
      <div className="font-mono text-[9px] tracking-[.13em] text-[var(--co-ink-4)]">{label}</div>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className="mt-1 block max-w-full truncate text-left text-[13px] text-[var(--co-ink)] underline-offset-2 hover:underline"
        >
          {value}
        </button>
      ) : (
        <div className="mt-1 truncate text-[13px] text-[var(--co-ink-2)]">{value}</div>
      )}
    </div>
  );
}

export default ProfileHeader;
